import { type PropsWithChildren, type ReactNode, useRef } from 'react';
import { TextFitGhost } from '@mentimeter/visualizations-react/components/text-fit-ghost';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { TextT } from '@mentimeter/ragnar-ui/text';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useIsLoadingFonts } from './useIsLoadingFonts';
import { DEFAULT_FONT_SIZES } from './constants';

interface TitleFontResizerProps {
  titleText: string;
  height: number;
  startingFontSize: string;
  ghostComponent?: ReactNode;
  fontSizes?: string[];
  titleStyles: TextT;
  shouldAnimate: boolean;
}

/**
 * A wrapper around the title that will resize the font to fit the container
 * @param titleText - not really rendered but need to re-trigger calculations
 * @param height
 * @param startingFontSize - the starting point for the resizer, also the size for the empty string
 * @param fontSizes - array of font sizes to choose from
 * @param titleStyles
 * @param ghostComponent - component used for font size calculations only. For example, we don't want to render the actual title if it's too complex like the full tip tap editor
 * @param shouldAnimate
 * @param children - title component that will be rendered
 * @constructor
 */
export const TitleFontResizer = ({
  titleText,
  height,
  startingFontSize,
  fontSizes = DEFAULT_FONT_SIZES,
  titleStyles,
  ghostComponent,
  shouldAnimate,
  children,
}: PropsWithChildren<TitleFontResizerProps>) => {
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingFonts = useIsLoadingFonts();

  return (
    <Box
      ref={titleContainerRef}
      width="100%"
      height={`${height}px`}
      data-testid="title-font-resizer"
      extend={() => ({
        fontSize: startingFontSize,
        opacity: 0,
        transition: shouldAnimate
          ? 'opacity ease-in-out 200ms, transform ease-in-out 200ms'
          : 'unset',
      })}
    >
      <TextFitGhost
        text={titleText}
        fontSizes={fontSizes}
        width="100%"
        height={height}
        finalFontSizeContainerRef={titleContainerRef}
        isWaitingForFontToLoad={isLoadingFonts}
      >
        {(ref) => (
          <Text ref={ref} {...titleStyles} style={{ fontSize: '56px' }}>
            {ghostComponent ?? children}
          </Text>
        )}
      </TextFitGhost>
      <Text
        as="h1"
        {...titleStyles}
        fontSize="inherit"
        data-testid="resized-title"
        extend={() => ({ height: '100%' })}
      >
        {children}
      </Text>
    </Box>
  );
};
